import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Form, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { FcFinePrint } from "react-icons/fc";
import { FiSearch } from "react-icons/fi";
import Pagination from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, LS_AUTHORITY_KEY } from "../../../core/constants/common";
import EmailAuditService from "../../../core/service/EmailAuditService";
import "./EmailLog.scss";

const STATUS_BADGE = {
  SENT: "bg-badge-Active",
  FAILED: "bg-badge-Inactive",
  QUEUED: "bg-badge-Invited",
  BOUNCED: "bg-badge-High",
};

const EMAIL_TYPE_OPTIONS = [
  "DCE_SHARE",
  "DCE_SUBMITTED_FOR_REVIEW",
  "DCE_REVIEW_COMPLETED",
  "DCE_REMINDER",
  "DCE_RESHARE",
  "PASSWORD_CHANGED",
  "PASSWORD_RESET",
  "COMPANY_INVITATION",
  "USER_INVITATION",
  "MATERIALITY_RESHARE",
  "MATERIALITY_SHARE",
  "MATERIALITY_REMINDER",
];

const STATUS_OPTIONS = ["SENT", "FAILED", "QUEUED", "BOUNCED"];

function EmailLog({ t }) {
  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [emailLogs, setEmailLogs] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [emailTypeFilter, setEmailTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const getCompanyId = () => {
    try {
      const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
      return authority?.profile?.company?.id ?? "";
    } catch {
      return "";
    }
  };

  const listEmailLogs = useCallback((pageNumber, itemsPerPageVal, searchKeyVal, sortDirectionVal, emailTypeVal, statusVal) => {
    const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
    const pageIndex = pageNumber > 0 ? pageNumber - 1 : 0;
    const companyId = getCompanyId();
    EmailAuditService.getEmailAuditLogs(
      pageIndex,
      perPage,
      companyId,
      searchKeyVal ?? searchKey,
      `createdDate,${sortDirectionVal ?? sortDirection}&sort=id,${sortDirectionVal ?? sortDirection}`,
      emailTypeVal ?? emailTypeFilter,
      statusVal ?? statusFilter
    )
      .then((response) => {
        const seenRows = new Set();
        const uniqueLogs = (response.data ?? []).filter((log) => {
          const rowKey = [log.id, log.recipients, log.subject, log.emailType, log.status, log.createdDate].join("|");
          if (seenRows.has(rowKey)) return false;
          seenRows.add(rowKey);
          return true;
        });
        setItemTotalCount(response.headers["x-total-count"] ?? 0);
        setEmailLogs(uniqueLogs);
      })
      .catch(() => { });
  }, [itemsPerPage, searchKey, sortDirection, emailTypeFilter, statusFilter]);

  useEffect(() => {
    listEmailLogs(0, itemsPerPage);
  }, [listEmailLogs, itemsPerPage]);

  const handleSortByCreatedDate = () => {
    const nextDirection = sortDirection === "desc" ? "asc" : "desc";
    setCurrentPage(1);
    setSortDirection(nextDirection);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    setCurrentPage(1);
    listEmailLogs(0, itemsPerPage, value);
  };

  const handleEmailTypeChange = (e) => {
    const value = e.target.value;
    setEmailTypeFilter(value);
    setCurrentPage(1);
    listEmailLogs(0, itemsPerPage, searchKey, sortDirection, value);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setCurrentPage(1);
    listEmailLogs(0, itemsPerPage, searchKey, sortDirection, emailTypeFilter, value);
  };

  const renderErrorDetails = (errorDetails) => {
    if (!errorDetails) return "-";
    const isConcurrentConnectionsThrottled = /4\.3\.2/.test(errorDetails) && /concurrent/i.test(errorDetails);
    if (isConcurrentConnectionsThrottled) {
      return <span title={errorDetails}>{t("EmailLog.errorDetails.throttled.label")}</span>;
    }
    const isRecipientRejected = /^\s*(550|551|552|553|554)\b/.test(errorDetails);
    if (isRecipientRejected) {
      return <span title={errorDetails}>{t("EmailLog.errorDetails.recipientRejected.label")}</span>;
    }
    return errorDetails;
  };

  return (
    <div className="FormsList">
      <div className="List_header d-flex justify-content-between align-items-center">
        <h5 className="MH">{t("EmailLog.label")}</h5>
        <div className="EmailLog_search d-flex align-items-center">
          <Form.Select
            size="sm"
            className="me-2"
            value={emailTypeFilter}
            onChange={handleEmailTypeChange}
          >
            <option value="">{t("EmailLog.emailType.label")}</option>
            {EMAIL_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>
          <Form.Select
            size="sm"
            className="me-2"
            value={statusFilter}
            onChange={handleStatusChange}
          >
            <option value="">{t("Status.label")}</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Form.Select>
          {/* <FiSearch className="me-2" /> */}
          <Form.Control
            type="search"
            size="sm"
            placeholder={t("Search.label")}
            value={searchKey}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      <div className="List_Table_01">
        <div className="card List_table-card01">
          <Table responsive className="emailLog_table">
            <thead>
              <tr>
                <th>{t("EmailLog.recipients.label")}</th>
                {/* <th>{t("EmailLog.sender.label")}</th> */}
                <th>{t("Subject.label")}</th>
                <th>{t("Status.label")}</th>
                <th>{t("EmailLog.emailType.label")}</th>
                {/* <th>{t("EmailLog.retryCount.label")}</th> */}
                <th role="button" onClick={handleSortByCreatedDate} className="EmailLog_sortableHeader">
                  {t("CreatedDate.label")} {sortDirection === "desc" ? "▼" : "▲"}
                </th>
              </tr>
            </thead>
            <tbody>
              {emailLogs?.length > 0 ? (
                emailLogs.map((log, index) => (
                  <tr key={`${currentPage}-${index}`} className="emailLog_table_row">
                    <td className="emailLog_table_row_recipients">
                      {log.recipients}
                    </td>
                    {/* <td>{log.sender}</td> */}
                    <td className="emailLog_table_row_subject">
                      {log.subject}
                      {log.errorDetails && (
                        <p className="small text-danger"><b>Error Details :</b> {renderErrorDetails(log.errorDetails)}</p>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[log.status] ?? "bg-secondary"}`}>
                        {log.status}
                      </span>
                    </td>
                    <td>
                      <Badge bg="badge-Accepted">{log.emailType}</Badge>
                    </td>
                    {/* <td>{log.retryCount}</td> */}
                    <td className="emailLog_table_row_createdDate">{log.createdDate ? moment(log.createdDate).format("MMM DD, YYYY HH:mm") : ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    <div className="Nodatafound_box">
                      <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="p-2 List_footer_count">
            <Pagination
              itemsTotalCount={itemsTotalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              alwaysShown={true}
              setItemsPerPage={setItemsPerPage}
              pageClicked={(pageNum, perPage) => {
                listEmailLogs(pageNum, perPage, searchKey);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(EmailLog);
